/**
 * Prevents search forms from being submitted with empty or too short queries.
 * Uses mutation observers to bind to dynamically added search forms.
 */

function isSearchForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;

  const action = form.getAttribute('action') || form.action;
  if (!action) return false;

  try {
    return new URL(action, window.location.origin).pathname === '/search';
  } catch (error) {
    return action.trim() === '/search';
  }
}

function getSearchQueryInput(form) {
  return form.querySelector(
    'input[name="q"]:not([type="hidden"]), input[name="query"]:not([type="hidden"]), input[type="search"]:not([type="hidden"])'
  );
}

const MIN_SEARCH_QUERY_LENGTH = 2;

function isValidSearchQueryInput(queryInput) {
  const trimmedQuery = queryInput.value.trim();
  queryInput.setCustomValidity('');

  if (trimmedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
    queryInput.setCustomValidity(`Please enter at least ${MIN_SEARCH_QUERY_LENGTH} characters.`);
    return false;
  }

  return queryInput.checkValidity();
}

function blockInvalidSearchSubmission(form, event) {
  if (!isSearchForm(form)) return false;
  const queryInput = getSearchQueryInput(form);
  if (!queryInput) return false;

  if (isValidSearchQueryInput(queryInput)) return false;

  if (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  queryInput.focus();
  queryInput.reportValidity();
  return true;
}

function bindSearchFormGuard(form) {
  if (!isSearchForm(form) || form.dataset.searchGuardBound === 'true') return;
  form.dataset.searchGuardBound = 'true';

  const handleSubmitGuard = (event) => {
    blockInvalidSearchSubmission(form, event);
  };

  form.addEventListener(
    'submit',
    handleSubmitGuard,
    true
  );

  form.addEventListener(
    'click',
    (event) => {
      const submitControl = event.target.closest('button, input[type="submit"]');
      if (!submitControl) return;
      if ((submitControl.form || submitControl.closest('form')) !== form) return;
      handleSubmitGuard(event);
    },
    true
  );

  form.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter') return;
      handleSubmitGuard(event);
    },
    true
  );

  const queryInput = getSearchQueryInput(form);
  if (!queryInput) return;

  queryInput.addEventListener('input', () => {
    queryInput.setCustomValidity('');
  });
}

function bindSearchFormGuardsInNode(node) {
  if (!(node instanceof Element || node instanceof Document)) return;

  if (node instanceof HTMLFormElement) {
    bindSearchFormGuard(node);
    return;
  }

  node.querySelectorAll('form').forEach((form) => {
    bindSearchFormGuard(form);
  });
}

function startSearchFormGuards() {
  bindSearchFormGuardsInNode(document);

  if (!document.body) return;
  const searchFormObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        bindSearchFormGuardsInNode(node);
      });
    });
  });

  searchFormObserver.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startSearchFormGuards, { once: true });
} else {
  startSearchFormGuards();
}

const nativeFormSubmit = HTMLFormElement.prototype.submit;
HTMLFormElement.prototype.submit = function submitWithInvalidSearchGuard() {
  if (blockInvalidSearchSubmission(this)) return;

  nativeFormSubmit.call(this);
};

const nativeFormRequestSubmit = HTMLFormElement.prototype.requestSubmit;
if (nativeFormRequestSubmit) {
  HTMLFormElement.prototype.requestSubmit = function requestSubmitWithInvalidSearchGuard(submitter) {
    if (blockInvalidSearchSubmission(this)) return;
    nativeFormRequestSubmit.call(this, submitter);
  };
}
